import { useRouter } from 'next/router';
import { isMobile } from 'react-device-detect';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import cn from 'classnames';
// Types
import {
  shortenTimeString,
  ExpandedNotificationData,
} from 'helpers/notifications';
// Hooks
import { useMarkNotificationRead } from 'hooks';
// UI
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Components
import SingleNotificationActions from './SingleNotificationActions/SingleNotificationActions';
// Styles
import styles from './SingleNotification.module.scss';

type SingleNotificationProps = {
  notification: ExpandedNotificationData;
  isDropdown?: boolean;
  onUpdate?: () => void;
  onCloseDropdown?: () => void;
};

dayjs.extend(relativeTime);

const SingleNotification = ({
  notification,
  isDropdown = false,
  onUpdate,
  onCloseDropdown,
}: SingleNotificationProps) => {
  const router = useRouter();
  const { markNotificationRead } = useMarkNotificationRead();

  const { id, title, body, createdAt, isRead, redirectUrl } = notification;

  // different UI for small browser (mobile) or dropdown-instead of rows, we have columns (as per design)
  const showCompact = isDropdown || isMobile;

  // we don't show actions for notification rendered in the navigation dropdown (isDropdown flag)
  const showActions = !isDropdown && !!onUpdate;

  const createdAtString = dayjs(createdAt).from(dayjs());

  // this shortens the time string to "just now" or "1mo" instead of long strings like "a few seconds ago" or "1 month ago"
  const shortenedTimeString = shortenTimeString(createdAtString);

  const handleMarkNotificationRead = async () => {
    await markNotificationRead(id);
  };

  const handleNavigate = (e: React.MouseEvent | React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest(`.${styles.actionsWrapper}`)) {
      return;
    }
    if (redirectUrl) {
      handleMarkNotificationRead();

      const currentPath = router.asPath.split('?')[0];

      // Check if redirectUrl matches current location - used to close dropdown
      // and navigate to the target page
      let redirectPath = redirectUrl;
      if (redirectUrl.startsWith('CURRENT_PAGE?')) {
        redirectPath = currentPath;
      } else {
        redirectPath = redirectUrl.split('?')[0];
      }

      // Handle special case for tip payment visible - stay on current page with query params
      // this opens up the tip sent successfully modal
      if (redirectUrl.startsWith('CURRENT_PAGE?')) {
        const queryParams = redirectUrl.replace('CURRENT_PAGE', '');
        router.push({
          pathname: router.asPath.split('?')[0], // Get current path without existing query params
          query: {
            // Preserve existing query params from URL (not route params)
            ...Object.fromEntries(
              new URLSearchParams(router.asPath.split('?')[1] || '')
            ),
            // Add tip parameters
            ...Object.fromEntries(new URLSearchParams(queryParams)),
          },
        });
      } else {
        // if we're not already on the target page, navigate to it
        if (currentPath !== redirectPath) {
          router.push(redirectUrl);
        }
      }
      // After original action is done, close notifications dropdown
      if (isDropdown && currentPath === redirectPath && onCloseDropdown) {
        onCloseDropdown();
      }
    }
  };

  return (
    <div
      role="link"
      tabIndex={0}
      className={cn(styles.root, {
        [styles.unreadRoot]: !isRead,
        [styles.clickableRoot]: !!redirectUrl,
      })}
      onClick={handleNavigate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleNavigate(e);
      }}
    >
      <div className={styles.dotWrapper}>
        <MillionsIcon
          name="circleFilled"
          className={cn(styles.icon, { [styles.unreadIcon]: !isRead })}
        />
      </div>

      <div className={styles.detailsWrapper}>
        <div className={styles.titleWrapper}>
          <p className={cn(styles.title, { [styles.unreadText]: !isRead })}>
            {title}
          </p>

          {showActions && (
            <div className={styles.actionsWrapper}>
              <SingleNotificationActions
                notification={notification}
                onUpdate={onUpdate}
              />
            </div>
          )}
        </div>

        <div
          className={cn(styles.dataWrapper, {
            [styles.smallDataWrapper]: showCompact,
          })}
        >
          <div className={styles.bodyAndActionWrapper}>
            {!!body && (
              <p className={cn(styles.body, { [styles.unreadText]: !isRead })}>
                {body}
              </p>
            )}
          </div>
          <div
            className={cn(styles.timeWrapper, {
              [styles.smallTimeWrapper]: showCompact,
            })}
          >
            <p className={styles.time}>{shortenedTimeString}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleNotification;
